import type { Mnemonic } from "@nav-games/mnemonics";

/// Render a single mnemonic in the standard card layout — acronym +
/// name + summary + steps OR rule + worked example. Used by the
/// unified Mnemonics Reference section AND by each native domain
/// section (Sailing embeds Five Essentials, Tides embeds Rule of
/// Twelfths, etc.).

interface Props {
  m: Mnemonic;
  /// When set, hides the "When to use" text — useful when the
  /// surrounding context already makes it obvious.
  compact?: boolean;
}

export function MnemonicCard({ m, compact = false }: Props) {
  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 4,
        }}
      >
        <h3 style={{ margin: 0 }}>{m.acronym}</h3>
        <span className="small muted" style={{ fontWeight: 500 }}>
          {m.name}
        </span>
        {!m.canonical && (
          <span
            style={{
              fontSize: 11,
              background: "var(--bg-elev)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "1px 6px",
              color: "var(--text-muted)",
            }}
          >
            common variation
          </span>
        )}
      </div>

      <p className="small" style={{ margin: "4px 0 0" }}>
        {m.summary}
      </p>
      {!compact && (
        <p
          className="small muted"
          style={{ margin: "6px 0 0", fontStyle: "italic" }}
        >
          <strong>When to use:</strong> {m.whenToUse}
        </p>
      )}

      {m.steps.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 8,
            marginTop: 12,
          }}
        >
          {m.steps.map((s, i) => (
            <div
              key={`${m.id}-${i}`}
              style={{
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: 8,
                background: "var(--bg-elev)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "var(--accent)",
                    lineHeight: 1,
                    minWidth: 24,
                  }}
                >
                  {s.key}
                </span>
                <span style={{ fontWeight: 700 }}>{s.label}</span>
              </div>
              <p className="small" style={{ margin: 0 }}>
                {s.detail}
              </p>
            </div>
          ))}
        </div>
      )}

      {m.rule && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 13,
            whiteSpace: "pre-wrap",
            lineHeight: 1.5,
          }}
        >
          {m.rule}
        </div>
      )}

      {m.example && (
        <p
          className="small"
          style={{
            margin: "10px 0 0",
            padding: 8,
            background: "rgba(44, 122, 59, 0.06)",
            borderLeft: "3px solid #2c7a3b",
            borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
          }}
        >
          <strong>Example:</strong> {m.example}
        </p>
      )}
    </div>
  );
}
